/**
 * Validates that required environment variables are present.
 * This should be imported as early as possible in the application lifecycle.
 */

const requiredEnvVars = [
    'VITE_CLOUDFLARE_WORKER_URL',
    'VITE_R2_WORKER_URL',
    'VITE_GOOGLE_MAPS_API_KEY',
];

const missingVars: string[] = [];

// We use import.meta.env directly here because this file runs before anything else potentially
// But types might need to be correct.
// Since import.meta.env is typed typically via vite-env.d.ts, let's just cast or use as is.

// Provide a fallback for import.meta.env in non-Vite environments (e.g. Node scripts)
const env = (import.meta as any).env || { DEV: true, PROD: false, MODE: 'development' } as unknown as Record<string, string | undefined>;

for (const key of requiredEnvVars) {
    if (!env[key]) {
        missingVars.push(key);
    }
}

if (missingVars.length > 0) {
    const message = `CRITICAL: Missing required environment variables:\n${missingVars.join('\n')}\n\nPlease check your .env file.`;
    console.error(message);
    if (env.DEV) {
        console.warn('Application may not function correctly without these variables.');
    }
} else {

}

/**
 * Sanitizes and returns the worker URL, ensuring legacy medcode URLs 
 * are replaced with production espacero.com equivalents.
 */
export function getWorkerUrl(key: 'D1' | 'R2' | 'STRIPE' = 'D1'): string {
    // Use the local safe env with production fallbacks
    let url = '';

    if (key === 'D1') {
        url = env.VITE_CLOUDFLARE_WORKER_URL || 'https://api.espacero.com';
    } else if (key === 'R2') {
        url = env.VITE_R2_WORKER_URL || 'https://upload.espacero.com';
    } else if (key === 'STRIPE') {
        url = env.VITE_STRIPE_WORKER_URL || 'https://stripe.espacero.com';
    }

    return sanitizeHttpUrl(url);
}

function sanitizeHttpUrl(raw: string): string {
    const value = (raw || '').trim();
    if (!value) return '';

    try {
        const parsed = new URL(value);
        // Only allow http/https URLs to avoid accidental javascript:/data: URLs.
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '';
        return parsed.toString().replace(/\/$/, '');
    } catch {
        return '';
    }
}

const DEFAULT_ADMIN_EMAILS = 'gazi.andrej@gmail.com,geziendy@gmail.com';

/**
 * Returns normalized admin email allow-list from `VITE_ADMIN_EMAILS`.
 * Format: comma-separated list, e.g. "admin@espacero.com,owner@espacero.com".
 */
export function getAdminEmails(): string[] {
    const raw = env.VITE_ADMIN_EMAILS || DEFAULT_ADMIN_EMAILS;
    return raw
        .split(',')
        .map((email: string) => email.trim().toLowerCase())
        .filter(Boolean);
}

/**
 * Returns true only for explicit true-ish values (1/true/yes/on).
 * Useful for security-sensitive feature flags.
 */
export function getBooleanEnv(key: string, fallback = false): boolean {
    const value = (env[key] as string | undefined)?.trim().toLowerCase();
    if (value === undefined || value === '') return fallback;
    return ['1', 'true', 'yes', 'on'].includes(value);
}

export const validatedEnv = {
    CLOUDFLARE_WORKER_URL: getWorkerUrl('D1'),
    R2_WORKER_URL: getWorkerUrl('R2'),
    SITE_URL: env.VITE_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173'),
    DEV: env.DEV,
    PROD: env.PROD,
    MODE: env.MODE,
};
